import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { QueryPagination, createQueryOptions } from '../../query';
import { useOfferApi } from '../../src/offers/api';
import { offerKeys } from '../query-key-factory';
import { LocationSearch } from './locations.search.types';
import { LocationSearchResponse, ShowLocationResponse } from './locations.types';

const useListLocations = ({
  queryOptions,
  pagination,
}: {
  queryOptions?: LocationSearch;
  pagination?: QueryPagination;
}) => {
  const OfferApi = useOfferApi();

  const { queryString, queryObject, queryPagination } = createQueryOptions({
    search: queryOptions,
    pagination,
  });

  const getLocations = async () => {
    const { data } = await OfferApi.get<LocationSearchResponse>(`/locations?${queryString}`);
    return data;
  };

  const key = offerKeys.locations.all;
  return useQuery({
    queryKey: [...key, queryObject, queryPagination],
    queryFn: getLocations,
  });
};

const useListInfiniteLocations = ({
  queryOptions,
  initialLimit = 100,
}: {
  queryOptions?: LocationSearch;
  initialLimit?: number;
}) => {
  const OfferApi = useOfferApi();

  const getInfiniteData = async ({
    pageParam,
  }: {
    pageParam: { limit: number; offset: number };
  }) => {
    const { queryString } = createQueryOptions<LocationSearch>({
      search: queryOptions,
      pagination: {
        limit: pageParam.limit,
        offset: pageParam.offset,
      },
    });

    const { data } = await OfferApi.get<LocationSearchResponse>(`/locations?${queryString}`);
    return data;
  };

  const key = offerKeys.locations.all;

  return useInfiniteQuery({
    queryKey: [...key, queryOptions],
    queryFn: getInfiniteData,
    placeholderData: keepPreviousData,
    initialPageParam: { limit: initialLimit, offset: 0 },
    getNextPageParam: (lastPageData) => {
      const { meta } = lastPageData;
      const { has_more, limit, offset } = meta;
      if (!has_more) {
        return undefined;
      }
      return { limit, offset: offset + limit };
    },
  });
};

const useShowLocation = ({ locationId }: { locationId?: string }) => {
  const OfferApi = useOfferApi();

  const getLocation = async () => {
    const { data } = await OfferApi.get<ShowLocationResponse>(`/locations/${locationId}`);
    return data.location;
  };

  const queryKey = locationId ? offerKeys.locations.id(locationId).detail : [];

  return useQuery({
    queryKey: queryKey,
    queryFn: getLocation,
  });
};

export { useListInfiniteLocations, useListLocations, useShowLocation };
